'use client';
import { useEffect } from 'react';
 import Header from'@/components/common/Header';
 import Footer from'@/components/common/Footer';
 import BookingForm from'./components/BookingForm';
 import VehicleSelector from'./components/VehicleSelector';
 import PaymentSection from'./components/PaymentSection';
 import ExpectationTimeline from'./components/ExpectationTimeline';
 import BookingFAQ from'./components/BookingFAQ';
 import Icon from'@/components/ui/AppIcon';

export default function BookingPage() {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal')
    if (!reveals.length) return

    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    )
    reveals.forEach(el => observer.observe(el))

    return () => observer.disconnect()
  }, [])

  return (
    <>
      <Header />
      <main className="min-h-screen pt-20">
        {/* Page Header */}
        <section className="py-16 bg-gradient-to-br from-primary via-secondary to-primary text-white">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-dark border border-white/20 mb-6">
              <Icon name="BoltIcon" size={20} className="text-accent" />
              <span className="text-sm font-body font-medium">Fast & Easy Booking</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-display font-bold mb-4">
              Book Your Ride in 30 Seconds
            </h1>
            <p className="text-xl text-white/80 font-body max-w-2xl mx-auto">
              No signup required. Pay after your ride. Simple, transparent, reliable.
            </p>

            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-4 mt-12 max-w-2xl mx-auto">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-accent-foreground font-bold text-sm">
                  1
                </div>
                <span className="text-sm font-body hidden sm:inline">Location</span>
              </div>
              <div className="flex-1 h-0.5 bg-white/20" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm">
                  2
                </div>
                <span className="text-sm font-body hidden sm:inline">Vehicle</span>
              </div>
              <div className="flex-1 h-0.5 bg-white/20" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm">
                  3
                </div>
                <span className="text-sm font-body hidden sm:inline">Payment</span>
              </div>
            </div>
          </div>
        </section>

        {/* Booking Form Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Left Column - Form */}
              <div>
                <h2 className="text-2xl font-display font-bold text-primary mb-6">
                  Enter Your Trip Details
                </h2>
                <BookingForm />
              </div>

              {/* Right Column - Info */}
              <div className="space-y-6">
                <h2 className="text-2xl font-display font-bold text-primary mb-6">
                  Why Choose MaxiRide?
                </h2>
                
                {/* Benefits */}
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-success/5 border border-success/20">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                      <Icon name="BoltIcon" size={24} className="text-success" />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-primary mb-1">
                        Fast Pickup
                      </h3>
                      <p className="text-sm text-foreground/70 font-body">
                        Average wait time under 5 minutes in most areas
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 rounded-xl bg-secondary/5 border border-secondary/20">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                      <Icon name="CurrencyDollarIcon" size={24} className="text-secondary" />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-primary mb-1">
                        Transparent Pricing
                      </h3>
                      <p className="text-sm text-foreground/70 font-body">
                        See exact fare before booking. No surge pricing surprises
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 rounded-xl bg-accent/5 border border-accent/20">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                      <Icon name="ShieldCheckIcon" size={24} className="text-accent" />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-primary mb-1">
                        Verified Drivers
                      </h3>
                      <p className="text-sm text-foreground/70 font-body">
                        All drivers background-checked and highly rated
                      </p>
                    </div>
                  </div>
                </div>

                {/* Trust Badge */}
                <div className="p-6 rounded-2xl bg-gradient-to-br from-primary/5 to-secondary/5 border border-border">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-body font-semibold text-foreground/70">
                      Trusted by
                    </span>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Icon
                          key={`trust_star_${i}`}
                          name="StarIcon"
                          variant="solid"
                          size={16}
                          className="text-accent"
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-3xl font-display font-bold text-primary mb-2">
                    10M+ Riders
                  </p>
                  <p className="text-sm text-muted-foreground font-body">
                    Join millions of satisfied customers worldwide
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Vehicle Selection Section */}
        <section className="py-16 bg-muted/30">
          <div className="max-w-4xl mx-auto px-6">
            <VehicleSelector />
          </div>
        </section>

        {/* Payment Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <PaymentSection />
          </div>
        </section>

        {/* Expectation Timeline */}
        <ExpectationTimeline />

        {/* FAQ Section */}
        <BookingFAQ />
      </main>
      <Footer />
    </>
  )
}